import { ROUTES } from '@/core/config/routing'
import AuthenticateEmailsIcon from '@assets/images/authenticate-emails.svg?react'
import DetectThreats from '@assets/images/detect-threats.svg?react'
import MonitorCommunications from '@assets/images/monitor-communications.svg?react'

export const BENEFITS = [
    {
        id: 'authenticate-emails',
        icon: <AuthenticateEmailsIcon />,
        title: 'Authenticate emails',
        description:
            'Identify and stop sophisticated impersonation techniques targeting your company.',
    },
    {
        id: 'detect-threats',
        icon: <DetectThreats />,
        title: 'Detect threats',
        description:
            'Identify attempts to use employee identities for business email compromise.',
    },
    {
        id: 'monitor-communications',
        icon: <MonitorCommunications />,
        title: 'Monitor communications',
        description:
            'Continuously check incoming and outgoing emails for suspicious behavior.',
    },
]

export const TAB_LIST: TTabsList = [
    {
        id: 'fraud-defense-tab-issues',
        tabTitle: ROUTES.fraud_defense.children.open_issues.displayName,
        path: ROUTES.fraud_defense.children.open_issues.path,
        analyticsEvent: 'SEM::navigate_to_fraud_defense_open_issues_page',
    },
    {
        id: 'fraud-defense-tab-configurations',
        tabTitle: ROUTES.fraud_defense.children.configurations.displayName,
        path: ROUTES.fraud_defense.children.configurations.path,
    },
]

export const convertEnumName = (name: string): string => {
    return name
        .split('_')
        .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

export const convertFdIssuesToNewIssueFormat = (
    usersIssues: (TfdIssueRaw | TBECIssueRaw)[]
): TFraudDefenseIssue[] => {
    const groupedIssues = usersIssues.reduce(
        (acc, issue) => {
            if (issue.issue_type === 'FRAUD_DEFENSE') {
                const key = `${issue.email_data.date_sent}_${issue.email_data.subject}_${issue.finding_id}_${issue.email_data.sender.email_address}`
                if (!acc[key]) {
                    acc[key] = {
                        ...issue,
                        assets: [issue.asset],
                        recipients: issue.email_data.to_recipients,
                    }
                } else {
                    acc[key].assets.push(issue.asset)
                    acc[key].recipients = [
                        ...acc[key].recipients,
                        ...issue.email_data.to_recipients,
                    ]
                }
                return acc
            }
            return acc
        },
        {} as Record<string, any>
    )

    const fraudDefenseIssues = Object.values(groupedIssues).map((issue) => ({
        ...issue,
        id: issue.id,
        issue_category: issue.category as TIssueCategory,
        issueCategory: convertEnumName(issue.category as string),
        issueName: issue.name,
        severity: issue.severity as TIssueSeverity,
        status: issue.status as TIssueStatus,
        emailTime: issue.email_data.date_sent,
        subject: issue.email_data.subject,
        sender: {
            emailAddress: issue.email_data.sender.email_address,
            name: issue.email_data.sender.name,
        },
        recipients: Array.from(
            new Map(
                issue.recipients.map((recipient: any) => [
                    recipient.email_address,
                    {
                        emailAddress: recipient.email_address,
                        name: recipient.name,
                    },
                ])
            ).values()
        ),
        assets: issue.assets.map((asset: any) => {
            return {
                id: asset.id,
                hostname: asset.value,
                asset_type: asset.type || asset.asset_type,
                external_issue_id: asset.external_issue_id,
            }
        }),
    }))

    return fraudDefenseIssues
}

const earliestOpenCreatedAt = (
    current: string | undefined,
    issue: TBECIssueRaw
): string | undefined => {
    if (issue.status !== 'open' || !issue.created_at) {
        return current
    }
    if (!current) {
        return issue.created_at
    }

    return new Date(issue.created_at).getTime() < new Date(current).getTime()
        ? issue.created_at
        : current
}

export const convertBecIssuesToNewIssueFormat = (
    usersIssues: (TBECIssueRaw | TfdIssueRaw)[]
): TBECIssue[] => {
    const groupedIssues = usersIssues.reduce(
        (acc, issue) => {
            if (issue.issue_type === 'INBOX_RULE') {
                if (!acc[issue.user_id]) {
                    acc[issue.user_id] = {
                        ...issue,
                        created_at: earliestOpenCreatedAt(undefined, issue),
                        assets:
                            issue.status === 'open'
                                ? [
                                      {
                                          asset_type: issue.asset.asset_type,
                                          hostname: issue.asset.value,
                                          id: issue.asset.external_issue_id,
                                      },
                                  ]
                                : [],
                        rule_data: [
                            {
                                ...issue.rule_data,
                                status: issue.status,
                            },
                        ],
                    }
                } else {
                    // The group id is used downstream to match the user's
                    // open issues, so prefer the first open issue's id even
                    // when a resolved issue was encountered first.
                    if (
                        issue.status === 'open' &&
                        acc[issue.user_id].status !== 'open'
                    ) {
                        acc[issue.user_id].id = issue.id
                        acc[issue.user_id].status = 'open'
                    }

                    issue.status === 'open' &&
                        acc[issue.user_id].assets.push({
                            asset_type: issue.asset.asset_type,
                            hostname: issue.asset.value,
                            id: issue.asset.external_issue_id,
                        })

                    acc[issue.user_id].rule_data.push({
                        ...issue.rule_data,
                        status: issue.status,
                    })

                    acc[issue.user_id].created_at = earliestOpenCreatedAt(
                        acc[issue.user_id].created_at,
                        issue
                    )
                }
            }
            return acc
        },
        {} as Record<string, any>
    )
    return Object.values(groupedIssues).map((issue) => ({
        ...issue,
        id: issue.id,
        issueCategory: convertEnumName(issue.issue_category),
        external_finding_id: issue.id,
        category: issue.issue_category,
        issueName: issue.name,
        severity: issue.severity as TIssueSeverity,
        status: issue.rule_data
            .map((rule: TBECRule) => rule.status)
            .includes('open')
            ? 'open'
            : 'resolved',
        subject: issue.subject,
        rule_data: issue.rule_data,
        assets: issue.assets,
        hostname: issue.hostname,
    }))
}
